import React, {
    useEffect,
    useState,
    useRef,
    useImperativeHandle,
    forwardRef,
  } from "react";
  import Highcharts from "highcharts/highstock";
  import HighchartsReact from "highcharts-react-official";
  import HC_more from "highcharts/highcharts-more";
  import exporting from "highcharts/modules/exporting";
import mouseWheelZoom from "highcharts/modules/mouse-wheel-zoom";
  
  HC_more(Highcharts); // Initialize the 'highcharts-more' module
  exporting(Highcharts);
mouseWheelZoom(Highcharts); // Initialize the 'exporting' module
  
  const SPCSelectedTagGraphData = forwardRef(
    (
      {
        graphRealTimeData,
        selectTagUnit,
        tag_hh,
        tag_hi,
        tag_lo,
        tag_ll,
        seriesColor,
        // Support for multiple series (multiple tags)
        multipleSeriesData,
        multipleControlLimits,
      },
      ref
    ) => {
      const chartRef = useRef(null);
      const [optionCahrt, setOptionChart] = useState({
        chart: {
          type: "spline",
          backgroundColor: "#ffffff", // White background for empty chart
          height: 400, // Height to show empty space
        },
        title: {
          text: null,
        },
        xAxis: {
          categories: [],
          scrollbar: {
            enabled: true,
          },
        },
        yAxis: {
          title: {
            text: "",
          },
          plotLines: [],
        },
        series: [],
        colors: ["#FF0000", "#00FF00", "#0000FF", "#FF00FF", "#FFFF00"],
        exporting: {
          enabled: true, // Disable exporting options
        },
        credits: {
          enabled: false, // Disable credits
        },
      });
  
      // console.log("tag_ll", tag_ll);
      // console.log("tag_hi", tag_hi);
      // console.log("tag_hh", tag_hh);
      // console.log("graphRealTimeData", graphRealTimeData);
  
      // Prepare data with markers for violations when SPC mode is true
      const prepareSeriesData = () => {
        // Ensure graphRealTimeData is an array
        const data = Array.isArray(graphRealTimeData) ? graphRealTimeData : [];
  
        if (!data.length) {
          // Return empty array to ensure chart still renders
          return [];
        }
  
        // SPC mode: return data with markers for violations
        return data.map((row, index) => {
          const value = parseFloat(row.tagValue);
          const isViolation = row.isViolation === true;
          const ruleBreak = row.ruleBreak || [];
  
          const dataPoint = {
            y: value,
            ruleBreak: ruleBreak,
          };
  
          if (isViolation) {
            // Big red dot for violations
            dataPoint.marker = {
              enabled: true,
              radius: 8,
              fillColor: "#FF0000",
              lineColor: "#FF0000",
              lineWidth: 2,
              symbol: "circle",
            };
          } else {
            // Normal dot for regular points - use tag color if available, otherwise default
            const markerColor = seriesColor || "#4A90E2";
            dataPoint.marker = {
              enabled: true,
              radius: 3,
              fillColor: markerColor,
              lineColor: markerColor,
              lineWidth: 1,
              symbol: "circle",
              states: {
                hover: {
                  enabled: true,
                  radius: 5,
                },
              },
            };
          }
  
          return dataPoint;
        });
      };
  
      // Prepare plot line series for tooltip support (invisible series with wide hit area)
      const preparePlotLineSeries = () => {
        const plotLineSeries = [];
        const categories = graphRealTimeData.length
          ? graphRealTimeData.map((row) => row.tagTime)
          : [];
  
        // Helper function to check if a value is valid and numeric
        const isValidNumber = (val) => {
          return (
            val != null && val !== undefined && val !== "" && !isNaN(Number(val))
          );
        };
  
        // UCL series (invisible line with tooltip, positioned exactly on plot line)
        if (isValidNumber(tag_hh)) {
          const uclValue = Number(tag_hh);
          plotLineSeries.push({
            name: "UCL",
            data:
              categories.length > 0 ? categories.map(() => uclValue) : [uclValue],
            color: "transparent", // Invisible
            lineWidth: 8, // Wide hit area for easier hovering
            enableMouseTracking: true, // Enable tooltip
            marker: {
              enabled: false,
            },
            showInLegend: false,
            states: {
              hover: {
                lineWidth: 8, // Keep wide on hover
              },
            },
          });
        }
  
        // CL series (invisible line with tooltip, positioned exactly on plot line)
        if (isValidNumber(tag_hi)) {
          const clValue = Number(tag_hi);
          plotLineSeries.push({
            name: "CL",
            data:
              categories.length > 0 ? categories.map(() => clValue) : [clValue],
            color: "transparent", // Invisible
            lineWidth: 8, // Wide hit area for easier hovering
            enableMouseTracking: true, // Enable tooltip
            marker: {
              enabled: false,
            },
            showInLegend: false,
            states: {
              hover: {
                lineWidth: 8, // Keep wide on hover
              },
            },
          });
        }
  
        // LCL series (invisible line with tooltip, positioned exactly on plot line)
        if (isValidNumber(tag_ll)) {
          const lclValue = Number(tag_ll);
          plotLineSeries.push({
            name: "LCL",
            data:
              categories.length > 0 ? categories.map(() => lclValue) : [lclValue],
            color: "transparent", // Invisible
            lineWidth: 8, // Wide hit area for easier hovering
            enableMouseTracking: true, // Enable tooltip
            marker: {
              enabled: false,
            },
            showInLegend: false,
            states: {
              hover: {
                lineWidth: 8, // Keep wide on hover
              },
            },
          });
        }
  
        return plotLineSeries;
      };
  
      // Prepare series for multiple tags
      const prepareMultipleTagSeries = () => {
        if (!multipleSeriesData || !Array.isArray(multipleSeriesData)) {
          return [];
        }
  
        // Check if we have two tags (for dual Y-axis)
        const hasTwoTags = multipleSeriesData.length === 2;
  
        return multipleSeriesData.map((tagData, index) => {
          const tagName = tagData.tagName || `Tag ${index + 1}`;
          const tagColor = tagData.color || (index === 0 ? "#4A90E2" : "#6A5ACD");
          const tagDataPoints = tagData.data || [];
          const yAxisIndex = hasTwoTags ? index : 0; // Use different Y-axis for each tag if two tags
  
          // Prepare data points with violations - handle empty data
          const seriesData =
            tagDataPoints.length > 0
              ? tagDataPoints.map((row) => {
                  const value = parseFloat(row.tagValue);
                  const isViolation = row.isViolation === true;
                  const ruleBreak = row.ruleBreak || [];
  
                  const dataPoint = {
                    y: value,
                    ruleBreak: ruleBreak,
                  };
  
                  if (isViolation) {
                    dataPoint.marker = {
                      enabled: true,
                      radius: 8,
                      fillColor: "#FF0000",
                      lineColor: "#FF0000",
                      lineWidth: 2,
                      symbol: "circle",
                    };
                  } else {
                    // Different marker styles for Reports mode (2 tags)
                    // First tag (index 0): circles, Second tag (index 1): squares
                    const markerSymbol =
                      hasTwoTags && index === 1 ? "square" : "circle";
                    // Normal marker for regular points - ensure it's always visible
                    dataPoint.marker = {
                      enabled: true,
                      radius: 3,
                      fillColor: tagColor,
                      lineColor: tagColor,
                      lineWidth: 1,
                      symbol: markerSymbol,
                      states: {
                        hover: {
                          enabled: true,
                          radius: 5,
                        },
                      },
                    };
                  }
  
                  return dataPoint;
                })
              : []; // Return empty array if no data, but still create the series
  
          // Different marker styles for Reports mode (2 tags)
          // First tag (index 0): circles, Second tag (index 1): squares
          const markerSymbol = hasTwoTags && index === 1 ? "square" : "circle";
  
          const series = {
            name: tagName,
            data: seriesData, // Can be empty array
            color: tagColor,
            dashStyle: "Solid",
            lineWidth: 1,
            zIndex: 2, // Ensure data points appear above plot lines
            marker: {
              enabled: true,
              symbol: markerSymbol, // Set marker symbol for legend and data points
              states: {
                hover: {
                  enabled: true,
                },
              },
            },
            states: {
              hover: {
                lineWidth: 3, // Highlight line on hover
                enabled: true,
              },
            },
          };
          // Only set yAxis property when we have two tags (dual Y-axis)
          if (hasTwoTags) {
            series.yAxis = yAxisIndex;
          }
          return series;
        });
      };
  
      // Use multiple series if provided, otherwise use single tag series
      // Always create series even if data is empty to ensure chart renders
      const seriesFortag =
        multipleSeriesData &&
        Array.isArray(multipleSeriesData) &&
        multipleSeriesData.length > 0
          ? prepareMultipleTagSeries() // This will create series even with empty data arrays
          : [
              {
                name: selectTagUnit || "Tag",
                data: prepareSeriesData(), // Can be empty array
                color: seriesColor || "#4A90E2",
                dashStyle: "Solid",
                lineWidth: 1,
                zIndex: 2, // Ensure data points appear above plot lines
                marker: {
                  enabled: true,
                  states: {
                    hover: {
                      enabled: true,
                      radius: 5,
                    },
                  },
                },
                states: {
                  hover: {
                    lineWidth: 3, // Highlight line on hover
                    enabled: true,
                  },
                },
              },
            ];
  
      // Only use preparePlotLineSeries for single tag mode (not for multiple tags)
      const hasMultipleTags =
        multipleSeriesData &&
        Array.isArray(multipleSeriesData) &&
        multipleSeriesData.length > 0;
      let seriesData = hasMultipleTags
        ? seriesFortag
        : [...seriesFortag, ...preparePlotLineSeries()];
  
      // Validate series yAxis indices when we have two tags
      // Ensure all series have valid yAxis (0 or 1) or no yAxis (for single tag mode)
      if (hasMultipleTags && multipleSeriesData.length === 2) {
        seriesData = seriesData.map((series) => {
          // If series has yAxis property, ensure it's 0 or 1
          if (series.yAxis !== undefined && series.yAxis !== null) {
            if (series.yAxis !== 0 && series.yAxis !== 1) {
              // Invalid yAxis index - remove it (will use default axis 0)
              const { yAxis, ...seriesWithoutYAxis } = series;
              return seriesWithoutYAxis;
            }
          }
          return series;
        });
      }
  
      // Get categories from all series data (combine all time points)
      const getAllCategories = () => {
        if (
          multipleSeriesData &&
          Array.isArray(multipleSeriesData) &&
          multipleSeriesData.length > 0
        ) {
          const allTimes = new Set();
          multipleSeriesData.forEach((tagData) => {
            (tagData.data || []).forEach((row) => {
              if (row.tagTime) {
                allTimes.add(row.tagTime);
              }
            });
          });
          return Array.from(allTimes).sort();
        }
        const data = Array.isArray(graphRealTimeData) ? graphRealTimeData : [];
        return data.length ? data.map((row) => row.tagTime).filter(Boolean) : [];
      };
  
      const categories = getAllCategories();
  
      useEffect(() => {
        // Build plotLines array - always show lines if values exist (regardless of data points)
        const plotLines = [];
  
        // Helper function to check if a value is valid and numeric
        const isValidNumber = (val) => {
          return (
            val != null && val !== undefined && val !== "" && !isNaN(Number(val))
          );
        };
  
        // Handle multiple tags control limits
        // Determine if we have two tags (for dual Y-axis)
        // Base it on multipleSeriesData length since that determines number of Y-axes
        // Also ensure both tags actually exist (not just array length)
        const hasTwoTags =
          multipleSeriesData &&
          Array.isArray(multipleSeriesData) &&
          multipleSeriesData.length === 2 &&
          multipleSeriesData[0] &&
          multipleSeriesData[1];
  
        if (multipleControlLimits && Array.isArray(multipleControlLimits)) {
          multipleControlLimits.forEach((limits, index) => {
            // Only create plotLines if corresponding series data exists
            if (
              !multipleSeriesData ||
              !Array.isArray(multipleSeriesData) ||
              !multipleSeriesData[index]
            ) {
              return;
            }
  
            const tagName =
              multipleSeriesData[index]?.tagName || `Tag ${index + 1}`;
            const tagColor =
              multipleSeriesData[index]?.color ||
              (index === 0 ? "#4A90E2" : "#6A5ACD");
            const isLeftTag = index === 0; // First tag is left, second is right
  
            // UCL for this tag
            if (isValidNumber(limits.ucl)) {
              const uclValue = Number(limits.ucl);
              const plotLine = {
                value: uclValue,
                color: tagColor,
                dashStyle: "ShortDash",
                width: 2,
                label: {
                  text: `UCL: ${uclValue.toFixed(2)}`,
                  align: isLeftTag ? "left" : "right",
                  x: isLeftTag ? 10 : -10,
                  style: {
                    color: tagColor,
                    fontWeight: "bold",
                  },
                },
                zIndex: -1,
                tagIndex: index, // Store tag index to identify which tag this plotLine belongs to
              };
              // Always set yAxis property when we have two tags (dual Y-axis)
              // For single tag, don't set yAxis (will use default axis 0)
              if (hasTwoTags) {
                plotLine.yAxis = index;
              }
              plotLines.push(plotLine);
            }
  
            // CL for this tag
            if (isValidNumber(limits.cl)) {
              const clValue = Number(limits.cl);
              const plotLine = {
                value: clValue,
                color: "black",
                dashStyle: "ShortDash",
                width: 2,
                label: {
                  text: `CL: ${clValue.toFixed(2)}`,
                  align: isLeftTag ? "left" : "right",
                  x: isLeftTag ? 10 : -10,
                  style: {
                    fontWeight: "bold",
                  },
                },
                zIndex: -1,
                tagIndex: index, // Store tag index to identify which tag this plotLine belongs to
              };
              // Always set yAxis property when we have two tags (dual Y-axis)
              if (hasTwoTags) {
                plotLine.yAxis = index;
              }
              plotLines.push(plotLine);
            }
  
            // LCL for this tag
            if (isValidNumber(limits.lcl)) {
              const lclValue = Number(limits.lcl);
              const plotLine = {
                value: lclValue,
                color: tagColor,
                dashStyle: "ShortDash",
                width: 2,
                label: {
                  text: `LCL: ${lclValue.toFixed(2)}`,
                  align: isLeftTag ? "left" : "right",
                  x: isLeftTag ? 10 : -10,
                  style: {
                    color: tagColor,
                    fontWeight: "bold",
                  },
                },
                zIndex: -1,
                tagIndex: index, // Store tag index to identify which tag this plotLine belongs to
              };
              // Always set yAxis property when we have two tags (dual Y-axis)
              if (hasTwoTags) {
                plotLine.yAxis = index;
              }
              plotLines.push(plotLine);
            }
          });
        } else {
          // Single tag mode (backward compatibility)
          // UCL (tag_hh) - red dashed line - always show if value exists
          if (isValidNumber(tag_hh)) {
            const uclValue = Number(tag_hh);
            plotLines.push({
              value: uclValue,
              color: "red",
              dashStyle: "ShortDash",
              width: 2,
              label: {
                text: `UCL`,
                align: "left",
                x: 10,
                y: 10,
                style: {
                  fontWeight: "bold",
                },
              },
              zIndex: -1,
              events: {
                mouseover: function () {
                  // This will be handled by tooltip formatter
                },
              },
            });
          }
  
          // CL (tag_hi) - black dashed line - always show if value exists
          if (isValidNumber(tag_hi)) {
            const clValue = Number(tag_hi);
            plotLines.push({
              value: clValue,
              color: "black",
              dashStyle: "ShortDash",
              width: 2,
              label: {
                text: `CL`,
                align: "left",
                x: 10,
                style: {
                  fontWeight: "bold",
                },
              },
              zIndex: -1,
              events: {
                mouseover: function () {
                  // This will be handled by tooltip formatter
                },
              },
            });
          }
  
          // LCL (tag_ll) - red dashed line - always show if value exists
          if (isValidNumber(tag_ll)) {
            const lclValue = Number(tag_ll);
            plotLines.push({
              value: lclValue,
              color: "red",
              dashStyle: "ShortDash",
              width: 2,
              label: {
                text: `LCL`,
                align: "left",
                x: 10,
                style: {
                  fontWeight: "bold",
                },
              },
              zIndex: -1,
              events: {
                mouseover: function () {
                  // This will be handled by tooltip formatter
                },
              },
            });
          }
        }
  
        // Calculate yAxis min and max for each tag (independent scales)
        const calculateYAxisRange = (tagIndex) => {
          let yAxisMin = null;
          let yAxisMax = null;
  
          // Extract data values for this specific tag
          const dataValues = [];
          if (
            seriesData[tagIndex] &&
            seriesData[tagIndex].data &&
            Array.isArray(seriesData[tagIndex].data)
          ) {
            seriesData[tagIndex].data.forEach((point) => {
              const value = typeof point === "object" ? point.y : point;
              if (value != null && !isNaN(value)) {
                dataValues.push(Number(value));
              }
            });
          }
  
          // Extract control limit values for this tag
          const controlLimitValues = [];
          if (
            multipleControlLimits &&
            Array.isArray(multipleControlLimits) &&
            multipleControlLimits[tagIndex]
          ) {
            const limits = multipleControlLimits[tagIndex];
            if (isValidNumber(limits.ucl))
              controlLimitValues.push(Number(limits.ucl));
            if (isValidNumber(limits.cl))
              controlLimitValues.push(Number(limits.cl));
            if (isValidNumber(limits.lcl))
              controlLimitValues.push(Number(limits.lcl));
          } else if (tagIndex === 0) {
            // Single tag mode - use tag_hh, tag_hi, tag_ll
            if (isValidNumber(tag_hh)) controlLimitValues.push(Number(tag_hh));
            if (isValidNumber(tag_hi)) controlLimitValues.push(Number(tag_hi));
            if (isValidNumber(tag_ll)) controlLimitValues.push(Number(tag_ll));
          }
  
          if (dataValues.length > 0) {
            const allValues = [...dataValues, ...controlLimitValues];
            const overallMin = Math.min(...allValues);
            const overallMax = Math.max(...allValues);
  
            yAxisMin = overallMin;
            yAxisMax = overallMax;
  
            // Add 5% padding for better visibility
            const range = yAxisMax - yAxisMin;
            yAxisMin = yAxisMin - range * 0.05;
            yAxisMax = yAxisMax + range * 0.05;
          } else if (controlLimitValues.length > 0) {
            // No data yet, but we have control limits - use them to set range
            const minLimit = Math.min(...controlLimitValues);
            const maxLimit = Math.max(...controlLimitValues);
            const range = maxLimit - minLimit || 10; // Default range if all same
            yAxisMin = minLimit - range * 0.1;
            yAxisMax = maxLimit + range * 0.1;
          } else {
            // Default range
            yAxisMin = 0;
            yAxisMax = 100;
          }
  
          return { min: yAxisMin, max: yAxisMax };
        };
  
        // Get tag names for Y-axis titles and X-axis legend
        const leftTagName =
          hasTwoTags && multipleSeriesData[0]
            ? multipleSeriesData[0].tagName || "Tag 1"
            : selectTagUnit || "Tag";
        const rightTagName =
          hasTwoTags && multipleSeriesData[1]
            ? multipleSeriesData[1].tagName || "Tag 2"
            : null;
  
        // Calculate ranges for each Y-axis
        const leftYAxisRange = calculateYAxisRange(0);
        const rightYAxisRange = hasTwoTags ? calculateYAxisRange(1) : null;
  
        // Helper function to update plotLines visibility based on series visibility
        const updatePlotLinesVisibility = (chart) => {
          // Only apply for Reports mode with multiple tags
          if (
            hasTwoTags &&
            multipleSeriesData &&
            Array.isArray(multipleSeriesData) &&
            multipleSeriesData.length === 2
          ) {
            // Update plotLines visibility based on series visibility
            multipleSeriesData.forEach((tagData, tagIndex) => {
              const series = chart.series[tagIndex];
              if (series) {
                const isSeriesVisible = series.visible;
                const yAxisIndex = tagIndex;
                const yAxis = chart.yAxis[yAxisIndex];
  
                if (yAxis) {
                  // Get all plotLines for this yAxis
                  const plotLinesArray = yAxis.plotLinesAndBands || [];
                  plotLinesArray.forEach((plotLineObj) => {
                    // Check if this plotLine belongs to this tag
                    if (
                      plotLineObj.options &&
                      plotLineObj.options.tagIndex === tagIndex
                    ) {
                      // Show/hide plotLine and its label based on series visibility
                      if (isSeriesVisible) {
                        if (plotLineObj.svgElem) {
                          plotLineObj.svgElem.show();
                        }
                        // Show label if it exists
                        if (plotLineObj.label && plotLineObj.label.element) {
                          plotLineObj.label.element.style.display = "";
                        }
                      } else {
                        if (plotLineObj.svgElem) {
                          plotLineObj.svgElem.hide();
                        }
                        // Hide label if it exists
                        if (plotLineObj.label && plotLineObj.label.element) {
                          plotLineObj.label.element.style.display = "none";
                        }
                      }
                    }
                  });
                }
              }
            });
          }
        };
  
        const newOptions = {
          chart: {
            zooming: { mouseWheel: { enabled: true, type: "x" } },
            type: "spline",
            backgroundColor: "#ffffff", // White background for empty chart
            height: 400, // Height to show empty space
            events: {
              // Handle legend item clicks to show/hide control limit lines
              render: function () {
                updatePlotLinesVisibility(this);
              },
            },
          },
          title: {
            text: null,
          },
          xAxis: {
            categories: categories,
            scrollbar: {
              enabled: true,
            },
            labels: {
              formatter: function () {
                return this.value;
              },
              rotation: -45,
              style: {
                fontSize: "11px",
              },
            },
            title: {
              text:
                hasTwoTags && rightTagName
                  ? `${leftTagName} / ${rightTagName}`
                  : leftTagName,
            },
          },
          yAxis:
            hasTwoTags && multipleSeriesData[0] && multipleSeriesData[1]
              ? [
                  // Left Y-axis for first tag
                  {
                    title: {
                      text: leftTagName,
                      style: {
                        color: "#000000", // Black color
                      },
                    },
                    labels: {
                      style: {
                        color: "#000000", // Black color
                      },
                    },
                    plotLines: plotLines.filter((pl) => {
                      // Only include plotLines with yAxis === 0 (left axis)
                      return pl.yAxis === 0;
                    }),
                    min: leftYAxisRange.min,
                    max: leftYAxisRange.max,
                    opposite: false, // Left side
                  },
                  // Right Y-axis for second tag
                  {
                    title: {
                      text: rightTagName || "Tag 2",
                      style: {
                        color: "#000000", // Black color
                      },
                    },
                    labels: {
                      style: {
                        color: "#000000", // Black color
                      },
                    },
                    plotLines: plotLines.filter((pl) => {
                      // Only include plotLines with yAxis === 1 (right axis)
                      return pl.yAxis === 1;
                    }),
                    min: rightYAxisRange?.min || 0,
                    max: rightYAxisRange?.max || 100,
                    opposite: true, // Right side
                  },
                ]
              : {
                  title: {
                    text: leftTagName,
                    style: {
                      color: "#000000", // Black color
                    },
                  },
                  labels: {
                    style: {
                      color: "#000000", // Black color
                    },
                  },
                  plotLines: plotLines,
                  min: leftYAxisRange.min,
                  max: leftYAxisRange.max,
                },
          tooltip: {
            shared: false,
            formatter: function () {
              // Helper function to check if a value is valid and numeric
              const isValidNumber = (val) => {
                return (
                  val != null &&
                  val !== undefined &&
                  val !== "" &&
                  !isNaN(Number(val))
                );
              };
  
              // Get all three control limit values
              const uclValue = isValidNumber(tag_hh) ? Number(tag_hh) : null;
              const clValue = isValidNumber(tag_hi) ? Number(tag_hi) : null;
              const lclValue = isValidNumber(tag_ll) ? Number(tag_ll) : null;
  
              // Check if all three values are the same
              const allSame =
                uclValue !== null &&
                clValue !== null &&
                lclValue !== null &&
                uclValue === clValue &&
                clValue === lclValue;
  
              // Check if this is a plot line series (UCL, CL, LCL)
              if (
                this.series.name === "UCL" ||
                this.series.name === "CL" ||
                this.series.name === "LCL"
              ) {
                // If all values are the same, show all three in one tooltip
                if (allSame) {
                  let tooltip = `<b>Control Limits</b><br/>`;
                  if (uclValue !== null) {
                    tooltip += `<span style="color:red"><b>UCL</b></span>: <b>${uclValue}</b><br/>`;
                  }
                  if (clValue !== null) {
                    tooltip += `<span style="color:black"><b>CL</b></span>: <b>${clValue}</b><br/>`;
                  }
                  if (lclValue !== null) {
                    tooltip += `<span style="color:red"><b>LCL</b></span>: <b>${lclValue}</b>`;
                  }
                  return tooltip;
                } else {
                  // Values are different - show only the specific line value
                  const plotLineValue = this.y;
                  const color = this.series.name === "CL" ? "black" : "red";
                  return `<span style="color:${color}"><b>${this.series.name}</b></span>: <b>${plotLineValue}</b>`;
                }
              }
  
              // Regular data point tooltip
              let tooltip = `<b>${this.x}</b><br/>`;
              tooltip += `<span style="color:${this.color}">\u25CF</span> ${this.series.name}: <b>${this.y}</b>`;
  
              // Show rule break if exists
              if (
                this.point.ruleBreak &&
                Array.isArray(this.point.ruleBreak) &&
                this.point.ruleBreak.length > 0
              ) {
                tooltip += `<br/><br/><span style="font-weight:bold;">Rule Break:</span><span style="color:red;font-weight:bold;">${this.point.ruleBreak.join(
                  ", "
                )}</span>`;
              }
  
              return tooltip;
            },
          },
          series: seriesData,
          colors: ["#FF0000", "#00FF00", "#0000FF", "#FF00FF", "#FFFF00"],
          plotOptions: {
            series: {
              events: {
                // Handle legend item click to show/hide control limit lines
                legendItemClick: function (e) {
                  const series = this;
                  const chart = series.chart;
                  const seriesIndex = chart.series.indexOf(series);
  
                  // Only apply for Reports mode with multiple tags
                  if (
                    hasTwoTags &&
                    multipleSeriesData &&
                    Array.isArray(multipleSeriesData) &&
                    multipleSeriesData.length === 2 &&
                    seriesIndex < 2
                  ) {
                    // After a short delay, update plotLines based on new visibility
                    // The delay ensures the series visibility has been updated by Highcharts
                    setTimeout(() => {
                      updatePlotLinesVisibility(chart);
                    }, 10);
                  }
                },
              },
            },
          },
          exporting: {
            enabled: true,
          },
          credits: {
            enabled: false,
          },
        };
  
        setOptionChart(newOptions);
  
        // Force update chart if it exists
        if (chartRef.current && chartRef.current.chart) {
          chartRef.current.chart.update(newOptions, true);
        }
      }, [
        selectTagUnit,
        graphRealTimeData,
        tag_hh,
        tag_hi,
        tag_lo,
        tag_ll,
        multipleSeriesData,
        multipleControlLimits,
      ]);
  
      // Method to export chart as base64 image
      const exportChartAsBase64 = () => {
        return new Promise((resolve, reject) => {
          if (!chartRef.current || !chartRef.current.chart) {
            reject(new Error("Chart is not available"));
            return;
          }
  
          const chart = chartRef.current.chart;
  
          // Get SVG from chart
          const svg = chart.getSVG();
  
          if (!svg) {
            reject(new Error("Chart SVG is not available"));
            return;
          }
  
          // Convert SVG to base64 PNG using canvas
          const svgData =
            "data:image/svg+xml;charset=utf-8," + encodeURIComponent(svg);
  
          const img = new Image();
          img.onload = function () {
            try {
              const canvas = document.createElement("canvas");
              canvas.width = img.width || chart.chartWidth;
              canvas.height = img.height || chart.chartHeight;
              const ctx = canvas.getContext("2d");
  
              // Draw white background
              ctx.fillStyle = "#ffffff";
              ctx.fillRect(0, 0, canvas.width, canvas.height);
  
              // Draw the SVG image
              ctx.drawImage(img, 0, 0);
  
              // Convert canvas to base64 PNG
              const base64String = canvas.toDataURL("image/png");
              resolve(base64String);
            } catch (error) {
              // Fallback: return SVG as base64
              const svgBase64 =
                "data:image/svg+xml;base64," +
                btoa(unescape(encodeURIComponent(svg)));
              resolve(svgBase64);
            }
          };
  
          img.onerror = function () {
            // Fallback: return SVG as base64
            const svgBase64 =
              "data:image/svg+xml;base64," +
              btoa(unescape(encodeURIComponent(svg)));
            resolve(svgBase64);
          };
  
          img.src = svgData;
        });
      };
  
      // Expose the export method via ref
      useImperativeHandle(ref, () => ({
        exportChartAsBase64,
      }));
  
      return (
        <div style={{ width: "100%" }}>
          <div style={{ width: "100%" }}>
            <HighchartsReact
              ref={chartRef}
              highcharts={Highcharts}
              options={optionCahrt}
              containerProps={{ style: { width: "100%" } }} // Set width to 100%
            />
          </div>
        </div>
      );
    }
  );
  
  SPCSelectedTagGraphData.displayName = "SPCSelectedTagGraphData";
  
  export default SPCSelectedTagGraphData;